import React, { useRef, useState } from "react";
import { X, Loader2, Upload, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { reportsApi } from "../lib/events";
import { extractError } from "../lib/api";
import { buildBulkErrorsCsv } from "../lib/bulkErrorsCsv";

const STATUS_STYLE = {
  valid: "text-emerald-600",
  changed: "text-emerald-600",
  unchanged: "text-[var(--gt-ink-3)]",
  invalid: "text-[var(--gt-accent)]",
  conflict: "text-[var(--gt-accent)]",
  failed: "text-[var(--gt-accent)]",
};

export function BulkAttendeeFixModal({ eventId, onClose, onApplied }) {
  const [step, setStep] = useState("upload"); // upload | preview | result
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [resultRows, setResultRows] = useState([]);
  const [resend, setResend] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const doPreview = async (file) => {
    setBusy(true);
    try {
      const res = await reportsApi.bulkFixPreview(eventId, file);
      setPreview(res);
      setStep("preview");
    } catch (e) {
      toast.error(extractError(e) || "Could not read that CSV.");
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    doPreview(f);
  };

  const doApply = async () => {
    const rows = (preview?.rows || [])
      .filter((r) => r.status === "valid")
      .map((r) => ({ ticket_id: r.ticket_id, attendee_name: r.new_name, attendee_email: r.new_email }));
    if (rows.length === 0) { toast.error("There are no valid changes to apply."); return; }
    setBusy(true);
    try {
      const res = await reportsApi.bulkFixApply(eventId, rows, resend);
      setResult(res.summary);
      setResultRows(res.rows || []);
      setStep("result");
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(extractError(e) || "Could not apply changes.");
    } finally {
      setBusy(false);
    }
  };

  const s = preview?.summary;

  const downloadErrorsCsv = () => {
    const csv = buildBulkErrorsCsv(preview?.rows || [], resultRows || []);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "bulk-attendee-fix-errors.csv";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const conflictCount = preview?.summary?.conflict || 0;
  const dataIssues = (result?.invalid || 0) + conflictCount + (result?.failed || 0);
  const resendIssues = result?.resend_failed || 0;
  const issueCount = dataIssues + resendIssues;
  const hasErrors = !!result && issueCount > 0;
  const issueMessage = dataIssues === 0 && resendIssues > 0
    ? `${resendIssues} ticket${resendIssues === 1 ? " was" : "s were"} updated, but the email could not be sent. Download details or use Resend ticket later.`
    : `${issueCount} row${issueCount === 1 ? "" : "s"} need${issueCount === 1 ? "s" : ""} attention — download ${issueCount === 1 ? "it" : "details"} to correct and re-upload.`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60" data-testid="bulk-fix-modal">
      <div className="bg-[var(--gt-bg)] border-2 border-[var(--gt-ink)] w-full max-w-3xl max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b-2 border-[var(--gt-ink)] px-5 py-3">
          <h3 className="font-heading text-lg font-black tracking-tight">Bulk fix attendee details</h3>
          <button type="button" onClick={onClose} data-testid="bulk-fix-close" className="p-1 hover:opacity-70"><X size={18} /></button>
        </div>

        <div className="px-5 py-4">
          {step === "upload" && (
            <div data-testid="bulk-fix-upload">
              <p className="text-sm text-[var(--gt-ink-2)] mb-2">
                Upload a CSV with columns <code>ticket_code</code>, <code>attendee_name</code>, <code>attendee_email</code>.
                Leave a cell blank to keep that value unchanged. Existing tickets are matched by code — no new tickets are created. Max 500 rows.
              </p>
              <p className="text-xs text-[var(--gt-ink-3)] mb-4">Tip: start from the Attendees CSV export — it already includes the ticket code.</p>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onPick} className="hidden" data-testid="bulk-fix-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                className="gt-btn-primary text-sm py-2 inline-flex items-center gap-2" data-testid="bulk-fix-choose-file">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Choose CSV
              </button>
              {fileName && <span className="ml-3 text-xs text-[var(--gt-ink-3)]">{fileName}</span>}
            </div>
          )}

          {step === "preview" && s && (
            <div data-testid="bulk-fix-preview">
              <div className="flex flex-wrap gap-2 mb-4 text-xs" data-testid="bulk-fix-summary">
                <span className="border-2 border-[var(--gt-ink)] px-2 py-1">Submitted {s.submitted}</span>
                <span className="border-2 border-emerald-600 text-emerald-700 px-2 py-1">Valid {s.valid}</span>
                <span className="border-2 border-[var(--gt-ink)] px-2 py-1">Unchanged {s.unchanged}</span>
                <span className="border-2 border-[var(--gt-accent)] text-[var(--gt-accent)] px-2 py-1">Invalid {s.invalid}</span>
                <span className="border-2 border-[var(--gt-accent)] text-[var(--gt-accent)] px-2 py-1">Conflicts {s.conflict}</span>
              </div>
              <div className="overflow-x-auto border-2 border-[var(--gt-ink)] max-h-[40vh]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[var(--gt-bg)] border-b-2 border-[var(--gt-ink)]">
                    <tr className="text-left">
                      <th className="py-2 px-2">Ticket</th>
                      <th className="py-2 px-2">Name (current → new)</th>
                      <th className="py-2 px-2">Email (current → new)</th>
                      <th className="py-2 px-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className="border-b border-[var(--gt-line)]" data-testid={`bulk-fix-row-${i}`}>
                        <td className="py-1.5 px-2 font-mono">{r.ticket_code || "—"}</td>
                        <td className="py-1.5 px-2">{r.name_changed ? `${r.current_name || "—"} → ${r.new_name}` : (r.current_name || "—")}</td>
                        <td className="py-1.5 px-2">{r.email_changed ? `${r.current_email || "—"} → ${r.new_email}` : (r.current_email || "—")}</td>
                        <td className={`py-1.5 px-2 font-semibold ${STATUS_STYLE[r.status] || ""}`}>
                          {r.status}{r.reason ? ` · ${r.reason}` : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer" data-testid="bulk-fix-resend-label">
                <input type="checkbox" checked={resend} onChange={(e) => setResend(e.target.checked)} data-testid="bulk-fix-resend-checkbox" />
                Resend updated tickets to changed email addresses
              </label>

              <div className="flex items-center justify-end gap-2 mt-5">
                <button type="button" onClick={() => { setStep("upload"); setPreview(null); }} className="gt-btn-secondary text-sm py-2" data-testid="bulk-fix-back">Back</button>
                <button type="button" onClick={doApply} disabled={busy || s.valid === 0} className="gt-btn-primary text-sm py-2 inline-flex items-center gap-2 disabled:opacity-60" data-testid="bulk-fix-apply">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Apply {s.valid} change{s.valid === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          )}

          {step === "result" && result && (
            <div data-testid="bulk-fix-result">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={18} className="text-emerald-600" />
                <span className="font-semibold">Bulk fix complete</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {[["Submitted", result.submitted], ["Changed", result.changed], ["Unchanged", result.unchanged],
                  ["Invalid", result.invalid], ["Failed", result.failed], ["Tickets resent", result.tickets_resent],
                  ["Resend failed", result.resend_failed]].map(([k, v]) => (
                  <div key={k} className="border-2 border-[var(--gt-ink)] p-2">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">{k}</div>
                    <div className="font-heading text-xl font-black">{v}</div>
                  </div>
                ))}
              </div>
              {(result.failed > 0 || result.resend_failed > 0) && (
                <div className="mt-3 text-xs text-[var(--gt-ink-2)] flex items-start gap-1.5">
                  <AlertTriangle size={13} className="text-[var(--gt-accent)] mt-0.5" />
                  Corrections were saved. Any failed resends can be sent later using the per-ticket “Resend” action.
                </div>
              )}
              {hasErrors && (
                <div className="mt-4 border-2 border-[var(--gt-accent)] bg-[var(--gt-accent)]/5 px-3 py-2 text-xs text-[var(--gt-ink)]" data-testid="bulk-fix-issue-message">
                  {issueMessage}
                </div>
              )}
              <div className="flex justify-between items-center mt-5">
                {hasErrors ? (
                  <button type="button" onClick={downloadErrorsCsv} className="gt-btn-secondary text-sm py-2 inline-flex items-center gap-1" data-testid="bulk-fix-download-errors">
                    <Upload size={14} className="rotate-180" /> Download errors CSV
                  </button>
                ) : <span />}
                <button type="button" onClick={onClose} className="gt-btn-primary text-sm py-2" data-testid="bulk-fix-done">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkAttendeeFixModal;
